class TlGallery extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.tl-gallery-slide'));
    this.thumbs = Array.from(this.querySelectorAll('.tl-thumb'));
    this.current = 0;
    this.zoomSrcs = this.slides.map(s => s.dataset.zoom || '');
    this.hasZoom = this.zoomSrcs.some(Boolean);

    this.querySelectorAll('.tl-gallery-stage [data-dir]').forEach(btn =>
      btn.addEventListener('click', () => this.go(this.current + parseInt(btn.dataset.dir, 10)))
    );

    this.thumbs.forEach(thumb =>
      thumb.addEventListener('click', () => this.go(parseInt(thumb.dataset.galleryTarget, 10)))
    );

    const main = this.querySelector('.tl-gallery-main');
    let swiped = false;
    if (main) {
      if (this.slides.length > 1) {
        let tx = 0;
        main.addEventListener('touchstart', e => { tx = e.touches[0].clientX; swiped = false; }, { passive: true });
        main.addEventListener('touchmove', () => { swiped = true; }, { passive: true });
        main.addEventListener('touchend', e => {
          const dx = tx - e.changedTouches[0].clientX;
          if (Math.abs(dx) > 40) this.go(this.current + (dx > 0 ? 1 : -1));
        }, { passive: true });
      }
      if (this.hasZoom) {
        main.style.cursor = 'zoom-in';
        main.addEventListener('click', () => { if (!swiped) this.openLightbox(this.current); });
      }
    }

    const zoomBtn = this.querySelector('.tl-gallery-zoom');
    if (zoomBtn) zoomBtn.addEventListener('click', e => { e.stopPropagation(); this.openLightbox(this.current); });

    if (this.hasZoom) this.initLightbox();
  }

  go(idx) {
    const total = this.slides.length;
    if (total === 0) return;
    idx = ((idx % total) + total) % total;
    this.slides.forEach((s, i) => s.classList.toggle('active', i === idx));
    this.thumbs.forEach((t, i) => t.classList.toggle('active', i === idx));
    this.current = idx;
    if (this.lightbox && this.lightbox.classList.contains('open')) this.renderLightbox();
  }

  initLightbox() {
    this.lightbox = this.querySelector('.tl-lightbox');
    if (!this.lightbox) return;
    this.lbImg = this.lightbox.querySelector('.tl-lightbox-img');
    this.lbWrap = this.lightbox.querySelector('.tl-lightbox-img-wrap');
    this.lbCounter = this.lightbox.querySelector('.tl-lightbox-counter-cur');

    this.lightbox.querySelector('.tl-lightbox-close')
      .addEventListener('click', () => this.closeLightbox());

    this.lightbox.querySelectorAll('.tl-lightbox-arrow').forEach(btn =>
      btn.addEventListener('click', e => { e.stopPropagation(); this.go(this.current + parseInt(btn.dataset.dir, 10)); })
    );

    // Click empty area (backdrop/stage/wrap) closes; clicks on image/controls stop propagation.
    this.lightbox.addEventListener('click', e => {
      if (e.target === this.lightbox || e.target === this.lbWrap || e.target.classList.contains('tl-lightbox-stage')) {
        this.closeLightbox();
      }
    });

    this.lbImg.addEventListener('click', e => { e.stopPropagation(); this.toggleZoom(e); });
    this.lbWrap.addEventListener('mousemove', e => this.panZoom(e));
    this.lbWrap.addEventListener('touchmove', e => { if (this.lightbox.classList.contains('zoomed')) this.panZoom(e.touches[0]); }, { passive: true });

    this._onKey = e => {
      if (!this.lightbox.classList.contains('open')) return;
      if (e.key === 'Escape') this.closeLightbox();
      else if (e.key === 'ArrowLeft') this.go(this.current - 1);
      else if (e.key === 'ArrowRight') this.go(this.current + 1);
    };
    document.addEventListener('keydown', this._onKey);
  }

  openLightbox(idx) {
    this.current = idx;
    this.renderLightbox();
    this.lightbox.classList.add('open');
    document.body.style.overflow = 'hidden';
    this._lastFocus = document.activeElement;
    this.lightbox.querySelector('.tl-lightbox-close').focus();
  }

  closeLightbox() {
    this.lightbox.classList.remove('open', 'zoomed');
    this.lbImg.style.transformOrigin = '';
    document.body.style.overflow = '';
    if (this._lastFocus && this._lastFocus.focus) this._lastFocus.focus();
  }

  renderLightbox() {
    const src = this.zoomSrcs[this.current];
    if (src) this.lbImg.src = src;
    this.lightbox.classList.remove('zoomed');
    this.lbImg.style.transformOrigin = '';
    if (this.lbCounter) this.lbCounter.textContent = this.current + 1;
  }

  toggleZoom(e) {
    const zoomed = this.lightbox.classList.toggle('zoomed');
    if (zoomed) this.panZoom(e);
    else this.lbImg.style.transformOrigin = '';
  }

  panZoom(e) {
    if (!e || !this.lightbox.classList.contains('zoomed')) return;
    const r = this.lbWrap.getBoundingClientRect();
    const x = Math.min(100, Math.max(0, ((e.clientX - r.left) / r.width) * 100));
    const y = Math.min(100, Math.max(0, ((e.clientY - r.top) / r.height) * 100));
    this.lbImg.style.transformOrigin = `${x}% ${y}%`;
  }
}

customElements.define('tl-gallery', TlGallery);

class TlVariantPicker extends HTMLElement {
  connectedCallback() {
    const id = this.dataset.section;
    this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
    this.idInput = document.getElementById(`tl-variant-id-${id}`);
    this.stock = document.getElementById(`tl-stock-${id}`);
    this.atc = document.getElementById(`tl-atc-${id}`);
    this.priceRow = document.getElementById(`tl-price-${id}`);
    this.atcPrice = document.getElementById(`tl-atc-price-${id}`);
    this.stickyPrice = document.getElementById(`tl-sticky-price-${id}`);
    this.stickyAtc = document.getElementById(`tl-sticky-atc-${id}`);
    this.upsell = document.querySelector(`tl-upsell[data-section="${id}"]`);
    this.shipNote = document.getElementById(`tl-ship-${id}`);
    this.addEventListener('change', () => this.update());
  }

  setButtonState(button, available) {
    if (!button) return;
    button.disabled = !available;
    button.querySelector('.tl-atc-in').hidden = !available;
    button.querySelector('.tl-atc-out').hidden = available;
  }

  update() {
    const options = Array.from(this.querySelectorAll('fieldset'), f => f.querySelector('input:checked')?.value);
    const variant = this.variants.find(v => v.options.every((o, i) => o === options[i]));
    if (!variant) return;

    this.idInput.value = variant.id;
    this.setButtonState(this.atc, variant.available);
    this.setButtonState(this.stickyAtc, variant.available);
    if (this.shipNote) this.shipNote.hidden = !variant.available;
    this.stock.classList.toggle('tl-stock--out', !variant.available);
    this.stock.querySelector('.tl-stock__label').textContent =
      variant.available ? this.stock.dataset.labelIn : this.stock.dataset.labelOut;
    this.renderPrice(variant);

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }

  renderPrice(variant) {
    this.priceRow.querySelector('.tl-pdp-price').textContent = variant.price;
    if (this.upsell) {
      this.upsell.update(variant.price, variant.priceWithUpsell, variant.available);
    } else {
      this.atcPrice.textContent = `· ${variant.price}`;
      if (this.stickyPrice) this.stickyPrice.textContent = variant.price;
    }
    let compare = this.priceRow.querySelector('.tl-pdp-price-secondary');
    if (!variant.compare) {
      compare?.remove();
      return;
    }
    if (!compare) {
      compare = document.createElement('span');
      compare.className = 'tl-pdp-price-secondary';
      this.priceRow.querySelector('.tl-pdp-price').after(compare);
    }
    compare.textContent = variant.compare;
  }
}

customElements.define('tl-variant-picker', TlVariantPicker);

class TlUpsell extends HTMLElement {
  connectedCallback() {
    const id = this.dataset.section;
    this.input = this.querySelector('input');
    this.card = this.querySelector('.tl-upsell-card');
    this.atcPrice = document.getElementById(`tl-atc-price-${id}`);
    this.stickyPrice = document.getElementById(`tl-sticky-price-${id}`);
    this.addEventListener('change', () => this.render());
  }

  update(base, total, kitAvailable) {
    this.dataset.base = base;
    this.dataset.total = total;
    const enabled = kitAvailable && this.dataset.addonAvailable === 'true';
    this.input.disabled = !enabled;
    if (!enabled) this.input.checked = false;
    this.card.classList.toggle('tl-upsell-card--out', !enabled);
    this.render();
  }

  render() {
    const price = this.input.checked ? this.dataset.total : this.dataset.base;
    this.atcPrice.textContent = `· ${price}`;
    if (this.stickyPrice) this.stickyPrice.textContent = price;
  }
}

customElements.define('tl-upsell', TlUpsell);

class TlStickyBuy extends HTMLElement {
  connectedCallback() {
    const atc = document.getElementById(`tl-atc-${this.dataset.section}`);
    if (!atc) return;
    // Show only once the main Add to cart has scrolled up out of view.
    this.observer = new IntersectionObserver(([entry]) => {
      this.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
    });
    this.observer.observe(atc);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}

customElements.define('tl-sticky-buy', TlStickyBuy);
